import {
    AbsenceStatus,
    type App,
    type DateRange,
    GetAbsencesParams,
    GetAvailabilitiesParams,
    GetRosterNotesParams,
    GetSchoolHolidaysParams,
    TimeEntryType,
} from "@pentacode/core";

type Ctx = { signal: AbortSignal };

/** Absences, their time entries and the vacation balances move together, so one prefix invalidates all three. */
export const absencesKey = ["absences"] as const;
export const availabilitiesKey = ["availabilities"] as const;
export const rosterNotesKey = ["roster-notes"] as const;
export const schoolHolidaysKey = ["school-holidays"] as const;

export function absencesQuery(app: App, range: DateRange) {
    return {
        queryKey: [...absencesKey, "list", range.from, range.to] as const,
        queryFn: ({ signal }: Ctx) =>
            app.api.getAbsences(
                new GetAbsencesParams({
                    ...range,
                    status: [AbsenceStatus.Approved, AbsenceStatus.Requested],
                }),
                { signal }
            ),
    };
}

export function absenceTimeEntriesQuery(app: App, range: DateRange) {
    return {
        queryKey: [...absencesKey, "time-entries", range.from, range.to] as const,
        // app.getTimeEntries resolves the entries against app state and takes no abort signal
        queryFn: () => app.getTimeEntries({ ...range, type: [TimeEntryType.Free, TimeEntryType.CompDay] }),
    };
}

export function vacationBalancesQuery(app: App, range: DateRange) {
    return {
        queryKey: [...absencesKey, "vacation-balances", range.from, range.to] as const,
        queryFn: ({ signal }: Ctx) =>
            app.api.getVacationBalances({ ...range, filters: [], resolution: "year" }, { signal }),
    };
}

export function availabilitiesQuery(app: App, range: DateRange, employeeIds: number[]) {
    return {
        queryKey: [...availabilitiesKey, range.from, range.to, employeeIds] as const,
        queryFn: ({ signal }: Ctx) =>
            app.api.getAvailabilities(new GetAvailabilitiesParams({ ...range, employees: employeeIds }), { signal }),
    };
}

export function schoolHolidaysQuery(app: App, range: DateRange) {
    const country = app.company.country;
    const states = app.venues.map((venue) => venue.state);
    return {
        queryKey: [...schoolHolidaysKey, range.from, range.to, country, states] as const,
        queryFn: ({ signal }: Ctx) =>
            app.api.getSchoolHolidays(new GetSchoolHolidaysParams({ ...range, country, states }), { signal }),
    };
}

export function rosterNotesQuery(app: App, range: DateRange) {
    return {
        queryKey: [...rosterNotesKey, range.from, range.to] as const,
        queryFn: ({ signal }: Ctx) => app.api.getRosterNotes(new GetRosterNotesParams(range), { signal }),
    };
}
